import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Provider } from 'react-redux';
import { createSseCon } from './api/be-connector.js';
import { getAttrValue } from './api/propChecker.js';
import { AppManager } from './components/AppManager.js';
import GenericWrapper from './components/GenericWrapper.js';
import { PageIdContext } from './components/pageIdContext.js';
import { EditorUpdateContext } from './components/editorUpdateContext.js';
import 'primereact/tabview';
import 'primereact/accordion';
import 'primereact/inputtext';
import { store } from './store/index.js';
import 'uuid';
import 'primereact/dropdown';
import 'primereact/selectbutton';
import 'primereact/colorpicker';
import './modules/code2/customComponents/colorPicker/colorPicker.css.js';
import 'primereact/inputswitch';
import 'primereact/inputtextarea';
import 'primereact/inputnumber';
import './modules/code2/customComponents/properties/properties.css.js';
import 'primereact/utils';
import { setMode } from './store/reducers/enginePropertiesSlice.js';
import 'primereact/button';
import 'primereact/column';
import 'primereact/datatable';
import 'primereact/overlaypanel';
import './modules/code2/customComponents/C2DataModelTable/types/DataType.js';
import 'primereact/calendar';
import 'primereact/checkbox';
import 'primereact/listbox';
import 'primereact/confirmdialog';
import 'primereact/menu';
import './modules/code2/customComponents/C2DataModelTable/components/ColumnHeader.css.js';
import './modules/code2/customComponents/C2DataModelTable/components/Table.css.js';
import 'react-router-dom';
import './store/reducers/executionSlice.js';
import { setSubscribedVariables } from './store/reducers/flowSlice.js';
import { setVariables } from './store/reducers/variablesSlice.js';
import { getPagePathsById } from './util.js';
import { PagePathsContext } from './components/pagePathsContext.js';
import 'react-helmet';
import 'fast-deep-equal';
import { AppMetaContext } from './components/appMetaContext.js';
import './modules/code2/customComponents/C2Field/index.js';
import 'primereact/radiobutton';
import './modules/code2/customComponents/C2RadioButtonGroup/C2RadioButtonGroup.css.js';
import { SampleDataContext } from './components/sampleDataContext.js';
import './modules/code2/customComponents/C2Chart/C2Chart.css.js';
import './modules/code2/customComponents/C2Chart/C2ChartChild.js';
import './modules/code2/customComponents/C2Chart/C2Charts.css.js';
import './modules/code2/customComponents/C2Chart/highcharts.css.js';
import { SmartLayout } from '../SmartLayout/SmartLayout.js';
import 'react-final-form';
import './api/contexts.js';
import 'react-grid-layout';
import 'react-resize-detector';
import './components/dragDataContext.js';
import './components/customComponents/GridContainer.css.js';
import '@code2io/common-flow-utilities';
import '@fortawesome/pro-light-svg-icons';
import '@fortawesome/react-fontawesome';
import 'primereact/progressbar';
import 'primereact/rating';
import 'primereact/tag';
import 'primereact/tristatecheckbox';
import 'primereact/api';
import './modules/code2/customComponents/C2DataTable/C2DataTable.css.js';
import 'primereact/tooltip';
import 'lodash';
import { setClientId, setHeaders } from './store/reducers/applicationSlice.js';
import { setAuthenticate, setToken } from './store/reducers/authSlice.js';

function mapVariables(variables) {
    return {
        app: variables.appVars,
        page: variables.pageVars
    };
}
const renderer = (component, children, variables, pageId) => {
    let newChildren = [...children];
    if (children.length === 1 && typeof children[0] === "string") {
        const appVars = Object.fromEntries(Object.entries(variables.app).map(([key, variable]) => {
            return [key, variable.value];
        }));
        const pageVars = Object.fromEntries(Object.entries(variables.page[pageId]).map(([key, variable]) => {
            return [key, variable.value];
        }));
        newChildren = [getAttrValue(children[0], { app: appVars, page: pageVars })];
    }
    if (component.ignoreError) {
        return GenericWrapper({
            comp: component,
            children: newChildren,
            ignoreError: true
        });
    }
    return React.createElement(GenericWrapper, { comp: component }, newChildren);
};
const Engine = (props) => {
    const { mode, variables, clientId, headers } = props;
    const mappedVariables = mapVariables(variables);
    const [variablesInitialized, setVariablesInitialized] = useState(false);
    const ref = useRef(null);
    const autofocus = useCallback((element) => {
        if (element) {
            element.focus();
            ref.current = element;
        }
    }, []);
    useEffect(() => {
        store.dispatch(setMode(mode));
        store.dispatch(setVariables(mappedVariables));
        setVariablesInitialized(true);
    }, [mode, variables]);
    useEffect(() => {
        if (mode === "live" && props.authenticate) {
            store.dispatch(setAuthenticate(true));
        }
        if (mode === "live" && props.token) {
            store.dispatch(setToken(props.token));
        }
    }, []);
    useEffect(() => {
        let closeSseCon = null;
        if (clientId) {
            store.dispatch(setClientId(clientId));
            if (mode === "live") {
                closeSseCon = createSseCon(`${window._env_.DISPATCHER_URL}/sse?c2ClientId=${clientId}`);
            }
        }
        if (headers) {
            store.dispatch(setHeaders(headers));
        }
        return () => {
            if (closeSseCon) {
                closeSseCon();
            }
        };
    }, [clientId, headers]);
    if (!variablesInitialized) {
        return null;
    }
    else if (mode === "design") {
        const { metadata, handleDelete, handleClone, handleEdit, handleUngroup, onColumnOperation, engineUpdated, selectedElementIds, designableClassNames, designMode, onEngineClicked, sampleData } = props;
        const { components } = metadata;
        const metaItems = Object.keys(components).map((key) => {
            if (components[key].children.length > 0 &&
                key !== "rootDiv" &&
                key !== "container")
                return {
                    [key]: components[key].children
                        .map((child) => child.value)
                        .filter((child) => child !== undefined)
                };
        });
        metaItems.forEach((item) => {
            if (item) {
                const key = Object.keys(item)[0];
                item[key];
            }
        });
        const handleEngineUpdated = (payload) => {
            if (ref.current) {
                ref.current.focus();
            }
            engineUpdated(payload);
        };
        return (React.createElement("div", { className: "w-full h-full outline-none", tabIndex: -1, ref: autofocus, onKeyDown: (e) => {
                if ((e.key === "Delete" || e.key === "Backspace") &&
                    selectedElementIds.length === 1) {
                    const meta = metadata.components[selectedElementIds[0]];
                    if (meta) {
                        handleDelete(meta);
                    }
                }
            } },
            React.createElement(Provider, { store: store },
                React.createElement(PageIdContext.Provider, { value: metadata.id },
                    React.createElement(EditorUpdateContext.Provider, { value: handleEngineUpdated },
                        React.createElement(SampleDataContext.Provider, { value: sampleData }, React.createElement(SmartLayout, { model: {
                                rootId: metadata.rootComponentId,
                                componentMap: metadata.components
                            }, designMode: designMode, elementRenderer: (props) => renderer(props.meta, props.children, mappedVariables, metadata.id), handleUngroup: designMode === "basic" && handleUngroup
                                ? handleUngroup
                                : undefined, engineUpdated: handleEngineUpdated, onColumnOperation: onColumnOperation, handleDelete: handleDelete, handleClone: handleClone, handleEdit: handleEdit, componentList: components, selectedElementIds: selectedElementIds, getDesignableClassName: (ele) => {
                                if (typeof ele.props.className === "undefined") {
                                    return "";
                                }
                                return Object.keys(ele.props.className)
                                    .filter((className) => {
                                    return designableClassNames.some((designableClassName) => {
                                        if (designableClassName instanceof RegExp) {
                                            return (className.match(designableClassName) !== null);
                                        }
                                        return className === designableClassName;
                                    });
                                })
                                    .join(" ");
                            }, onEngineClicked: () => onEngineClicked() })))))));
    }
    const { pages, flows, appMeta } = props;
    const flowList = (function () {
        let flowList = {};
        let watchList = [];
        for (const flowId in flows) {
            flowList[flowId] = flows[flowId].manifest;
            if (flows[flowId].watchList)
                watchList = [
                    ...watchList,
                    ...flows[flowId].watchList
                ];
            store.dispatch(setSubscribedVariables(flows[flowId].watchList));
        }
        return { flowList, watchList };
    })();
    return (React.createElement(Provider, { store: store },
        React.createElement(AppMetaContext.Provider, { value: Object.assign(Object.assign({}, appMeta), { appName: props.appName }) },
            React.createElement(PagePathsContext.Provider, { value: getPagePathsById(appMeta) },
                React.createElement(AppManager, { pages: Object.values(pages), flows: flowList.flowList, watchList: flowList.watchList, variables: mappedVariables })))));
};
// @ts-expect-error
if (window.Cypress) {
    // @ts-expect-error
    window.engineStore = store;
}

export { Engine, renderer };
//# sourceMappingURL=Engine.js.map
